import { clefNames, isClefName } from 'shared/definitions/musicTheory/clefs';
import type { Accidental } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import utils from '../utils';
import KeySigPlayer from './keySigPlayer';
import * as Skills from '../uTheory.Skills/skills';
import Clef from '../uTheory.Notation/clef';
import KeySignature from '../uTheory.Notation/keySignature';
import Staff from '../uTheory.Notation/staff';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import Music from '../uTheory.Notation/music';
import { toHTML } from '../uTheory.Font/font';

type KeySigIDOptions = InteractiveOptions;

interface KeySigIDQuestion {
  question: {
    keyNote: string;
    keyQuality: string;
    keySig: KeySignature;
    clef: Clef;
  };
  text: string[];
  skills: string[];
}

export default class KeySigID extends InteractivePrototype {
  keySigPlayer: KeySigPlayer;

  answer: {
    note: string;
    accidental: Accidental;
  };

  skillsCalculator: SkillCalculator;

  maxQuestions: number;

  clef: Clef;

  declare questions: KeySigIDQuestion[];

  declare currentQuestion: KeySigIDQuestion;

  keySig: KeySignature;

  constructor(displayDiv: HTMLDivElement, options: KeySigIDOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = KeySigID.registry.skills;
    this.keySigPlayer = new KeySigPlayer();

    this.settings = {
      instructions: true,
      notation: {
        fixedWidth: 140,
        ariaTitle: {
          clef: false,
          barline: false,
        },
        ariaLive: 'assertive',
      },
      letters: {
        letterButtons: true,
        keySigButtons: { accidentals: true, arrowListeners: false },
        checkButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      instructions: {
        noWrap: true,
        maxPercent: 30,
      },
    };
    this.answer = {
      note: '',
      accidental: '',
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: KeySigIDOptions) {
    options.maxQuestions = options.maxQuestions || 10;
    this.skillsCalculator = new SkillCalculator();

    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  generateQuestions(questionStrings?: string[]) {
    this.questionPrompt = 'Name the key: <span id="keySigIDAnswerDiv" aria-live="polite">__</span>';
    if (this.options.drill) {
      this.questionPrompt += '<br /><small>{question} of {question}';
    }
    const options = this.options;
    const drill = options.drill !== undefined && options.drill === true;
    this.questions =
      drill === true
        ? this.generateDrillQuestions()
        : this.generateManualQuestions(questionStrings);
    this.maxQuestions = this.questions.length;
  }

  generateManualQuestions(questionStrings: string[] = this.options.questions) {
    return questionStrings.map((question, index) =>
      this.processQuestion(question, index, questionStrings)
    );
  }

  generateDrillQuestions() {
    const options = this.options;
    const generator = new QuestionGenerator();
    const maxQuestions = options.maxQuestions;
    const skillsWithoutZeroAccidentals = options.skills
      .map(Skills.makeString)
      .filter((s) => s !== 'a_minor' && s !== 'c_major');

    // it may happen that only a_minor or c_major were given as skills... then the question
    // generator would fail, so we add other similar skills.
    let questionsToPush = [];
    if (skillsWithoutZeroAccidentals.length === 0) {
      if (options.skills.map(Skills.makeString).find((s) => s === 'a_minor')) {
        skillsWithoutZeroAccidentals.push('sharp_minors');
        skillsWithoutZeroAccidentals.push('flat_minors');
        questionsToPush.push('A/m');
      }
      if (options.skills.map(Skills.makeString).find((s) => s === 'c_major')) {
        skillsWithoutZeroAccidentals.push('sharp_majors');
        skillsWithoutZeroAccidentals.push('flat_majors');
        questionsToPush.push('C/m');
      }
    }

    let generatedQuestions = generator.generate(
      skillsWithoutZeroAccidentals,
      maxQuestions,
      options
    );

    // Be sure we include C Major or A minor if those skills were requested:
    questionsToPush = questionsToPush.filter((key) => !generatedQuestions.includes(key));
    // console.log('questions to push 2...', questionsToPush);
    if (questionsToPush.length) {
      generatedQuestions.splice(0, generatedQuestions.length, ...generatedQuestions);
      generatedQuestions = generator.rejectDuplicates(
        generatedQuestions,
        generatedQuestions.length
      );
      console.log(`pushing ${questionsToPush}`);
    }

    generatedQuestions = KeySigID.addClefsToQuestions(generatedQuestions);

    return generatedQuestions.map((question, index) =>
      this.processQuestion(question, index, generatedQuestions)
    );
  }

  processQuestion(questionString: string, index: number = 0, arr: string[] = []): KeySigIDQuestion {
    const questionParts = questionString.split('/');
    const keyNote = questionParts[0];
    const keyQuality = questionParts[1];
    const clef = questionParts[2] || utils.shuffle(['treble', 'bass'])[0];
    const keySig = new KeySignature(keyNote, keyQuality);
    const skills = this.calculateSkill(keySig);
    if (!isClefName(clef)) {
      throw Error(`${clef} is not a clef name. Must be one of ${clefNames.join(', ')}`);
    }

    return {
      question: {
        keyNote: keySig.keyNote.toShortString(),
        keyQuality: keySig.quality,
        keySig,
        clef: new Clef(clef),
      },
      text: [`${index + 1}`, `${arr.length}`],
      skills,
    };
  }

  static addClefsToQuestions(questions: string[]): string[] {
    const clefs = [];

    for (let i = 0; i < questions.length; i += 2) {
      clefs[i] = '/bass';
      clefs[i + 1] = '/treble';
    }

    utils.shuffle(clefs);

    for (let i = 0; i < questions.length; i++) {
      questions[i] += clefs[i];
    }
    return questions;
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.addNoteToAnswer, this);
    this.letterPanel.addKeySigCallback(this.addAccidentalToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  initializeMusic() {
    this.clef = new Clef();

    const keySig = new KeySignature('C', 'Major');
    const staff = new Staff(this.clef);
    const measure = new Measure();
    const voice = new Voice(measure);

    voice.setStaff(staff);

    this.music = new Music(staff, voice, keySig);
  }

  newQuestion() {
    this.clearAnswer();
    super.newQuestion();
    this.progressTracker.start(this.targetSpeed);

    // Question text is format note/keyQuality/clef:
    const question = this.currentQuestion.question;
    const clef = question.clef;
    const keySig = question.keySig;

    this.displayAnswer();
    this.keySig = keySig;
    this.music.keySignatures[0] = keySig;
    this.clef.setName(clef);

    // Has to come after super, or this.currentQuestion is not assigned!
    this.notation.render();
  }

  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addNoteToAnswer(note: string) {
    this.answer.note = note;
    this.displayAnswer();
  }

  addAccidentalToAnswer(accidental: string) {
    if (accidental === 'delete') {
      if (this.answer.accidental) {
        this.answer.accidental = '';
      } else if (this.answer.note) {
        this.answer.note = '';
      }
    } else {
      this.answer.accidental = accidental as Accidental;
    }
    this.displayAnswer();
  }

  displayAnswer() {
    const answerDiv = document.getElementById('keySigIDAnswerDiv');
    const accidentalString = toHTML('accidental', this.answer.accidental || '');
    const shortQuality = this.currentQuestion.question.keyQuality.slice(0, 3);
    const noteNameString = this.answer.note + accidentalString || '_';
    answerDiv.innerHTML = `${noteNameString} ${shortQuality}`;
    this.resize();
  }

  calculateSkill(key: KeySignature) {
    // The true param tells us to include ET identify tonic skills.
    return this.skillsCalculator.getKeySignatureSkill(key, true);
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    console.log('checking answer');
    if (!this.options.testMode && !this.answer.note) {
      this.checkMark.warn('Click or type a letter name first.');
      return;
    }

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.currentQuestion.question.keyNote;
    const userAnswer = `${this.answer.note}${this.answer.accidental}`;
    console.log('questionAnswer', questionAnswer);
    console.log('userAnswer', userAnswer);
    const correct = userAnswer === questionAnswer;

    console.log('questionAnswer', questionAnswer);
    console.log('userAnswer', userAnswer);
    console.log('correct', correct);

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(correct);
      this.rightAnswer(1200);
      this.notation.redraw();
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
      this.playWrongAnswer();
    }
  }

  clearAnswer() {
    this.answer.accidental = '';
    this.answer.note = '';
  }

  playWrongAnswer() {
    this.keySigPlayer.playWrongAnswer();
  }

  playAnswer() {
    this.keySigPlayer.playAnswer(this.keySig);
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.notation.render();
    this.displayDiv.removeChild(this.lettersDiv);
    // this.displayDiv.removeChild(this.answerDiv);
    super.gameOver();
  }

  getAnswerAndData(correct: boolean) {
    console.log('getAnswerAndData', correct);
    const question = this.currentQuestion.question;
    const quality = question.keyQuality[0] === 'M' ? 'Major' : 'minor';

    const keyName = `${question.keyNote} ${quality}`;

    const answerAndData = {
      question: keyName,
      questionType: 'KeySigID',
      questionText: `Name the ${quality} key.`,
      questionSVG: this.notation.exportSVG(),
      studentAnswerText: `${this.answer.note}${this.answer.accidental} ${quality}`,
      correctAnswerText: keyName,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  static registry = {
    description: 'Identify Key Signatures',
    skills: ['a_minor', 'c_major', 'sharp_majors', 'sharp_minors', 'flat_majors', 'flat_minors'],
  };
}
